import { useEffect } from 'react';

import { bind } from 'bind-event-listener';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import {
	useCurrentActionAuth,
	useCurrentActionAuthActions,
} from '@atlassian/conversation-assistant-store/controllers/current-action-auth';

/**
 * Creating listener for forge app auth https://developer.atlassian.com/platform/outbound-auth/guides/authorizing-users/#step-2--detecting-the-result-of-the-dance
 *
 * Only binds the window message listener when there's an active auth flow (currentAuth is set).
 * This ensures we only listen for auth callbacks when needed, rather than all the time.
 */
export const ForgeAuthListener = () => {
	const publish = usePublish('ai-mate');
	const [{ currentAuth }] = useCurrentActionAuth();
	const { clearAuth } = useCurrentActionAuthActions();

	useEffect(() => {
		// Only bind the listener if there's an active auth flow
		if (!currentAuth) {
			return;
		}

		const handlerUnbind = bind(window, {
			type: 'message',
			listener: (event) => {
				const { data } = event;

				if (!data) {
					return;
				}

				if (!['outbound-auth:success', 'outbound-auth:failure'].includes(data.type)) {
					return;
				}

				const window = event.source as Window | null;

				// close the auth window, so this window gets focused
				window?.close?.();

				const is3pActionAuth = currentAuth.authType === '3p';

				if (data.type === 'outbound-auth:success') {
					publish({
						type: 'forge-auth-success',
						source: 'forge-auth-listener',
						data: {
							is3pActionAuth,
							agentMessageId: currentAuth.agentMessageId,
							authUrl: currentAuth.url,
						},
					});
				} else if (data.type === 'outbound-auth:failure') {
					publish({
						type: 'forge-auth-failure',
						data: {
							errorMessage: data.message || undefined,
							is3pActionAuth,
						},
						source: 'forge-auth-listener',
					});
				}

				// Clear the auth state after handling the callback
				// This prevents the listener from being triggered erroneously next time
				clearAuth();
			},
		});

		return handlerUnbind;
	}, [currentAuth, publish, clearAuth]);

	return null;
};
